import React, { useState, useEffect, useCallback } from 'react';
import { apiDelete, apiGet, apiPost, apiPut } from '../api';
import { useAuth } from '../context/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { 
  CreditCard, Image, Mail, MessageSquare, Star, Settings,
  Upload, Loader2, ExternalLink, CheckCircle, AlertCircle, Trash2, Plus, Tag,
  Code, Copy, TestTube2, RotateCcw, Save, Phone, Info, Globe, Lock, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatApiError } from '../lib/apiError';
import { compressImage } from '../lib/imageCompress';
import { CustomSenderIdSection } from '../components/CustomSenderIdSection';

// Event type labels for display
const EVENT_TYPE_LABELS = {
  'class_booked': 'Class Booked',
  'booking_cancelled_no_refund': 'Booking Cancelled (No Refund)',
  'booking_cancelled_refund': 'Booking Cancelled (Credit Refunded)',
  'waitlist_added': 'Added to Waitlist',
  'waitlist_spot_available': 'Waitlist Spot Available'
};

// Channel icons
const CHANNEL_ICONS = {
  'email': Mail,
  'sms': Phone,
  'whatsapp': MessageSquare
};

export const StudioSettingsPage = () => {
  const { api, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [studio, setStudio] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'studio');

  // Onboarding state
  const [onboardingStatus, setOnboardingStatus] = useState(null);
  
  // Brevo state
  const [brevoSettings, setBrevoSettings] = useState({ 
    api_key: '', sender_email: '', sender_name: '', enabled: false,
    sms_enabled: false, sms_sender: '', whatsapp_enabled: false, whatsapp_sender: ''
  });
  const [brevoLoading, setBrevoLoading] = useState(false);
  const [brevoAffiliateLink, setBrevoAffiliateLink] = useState('https://www.brevo.com');

  // WhatsApp template management
  const [whatsappTemplates, setWhatsappTemplates] = useState([]);
  const [whatsappTemplatesLoading, setWhatsappTemplatesLoading] = useState(false);
  const [whatsappDialogOpen, setWhatsappDialogOpen] = useState(false);
  const [whatsappForm, setWhatsappForm] = useState({ template_name: '', language: 'en', template_content: '' });
  const [whatsappSubmitting, setWhatsappSubmitting] = useState(false);
  
  // Cancellation settings
  const [cancellationSettings, setCancellationSettings] = useState({
    min_notice_hours: 24,
    refund_type: 'class_pass_credit',
    late_cancel_penalty: 0,
    // Iter-46al: per-studio waitlist priority window (minutes, 0-240, 30-min steps)
    waitlist_grace_minutes: 60,
  });

  // Reviews state
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);

  // Activity types state
  const [newActivityType, setNewActivityType] = useState('');
  const [activityTypeSaving, setActivityTypeSaving] = useState(false);

  // Message Templates state
  const [templates, setTemplates] = useState({});
  const [defaults, setDefaults] = useState({});
  const [variables, setVariables] = useState({});
  const [eventTypes, setEventTypes] = useState([]);
  const [channels, setChannels] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState('class_booked');
  const [editingChannel, setEditingChannel] = useState(null);
  const [editForm, setEditForm] = useState({ subject: '', content: '', is_active: true });
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testingTemplate, setTestingTemplate] = useState(null);
  const [testLoading, setTestLoading] = useState(false);

  // Widget settings state
  const [widgetSettings, setWidgetSettings] = useState({
    enabled: false,
    background_color: '#ffffff',
    text_color: '#1f2937',
    button_color: '#3b82f6',
    button_text_color: '#ffffff',
    accent_color: '#10b981',
    font_family: 'system-ui',
    border_radius: '8px',
    show_instructor: true,
    show_spots_remaining: true,
    show_activity_type: true
  });
  const [widgetLoading, setWidgetLoading] = useState(false);
  const [embedCode, setEmbedCode] = useState('');
  const [widgetUrl, setWidgetUrl] = useState('');

  // Plan features state
  const [planFeatures, setPlanFeatures] = useState({
    brevo_integration: false,
    widget_embedding: false,
    mobile_app: false
  });
  const [planName, setPlanName] = useState(null);
  const [hasSubscription, setHasSubscription] = useState(false);

  // Studio info state
  const [studioInfo, setStudioInfo] = useState({
    name: '',
    description: '',
    address: '',
    city: '',
    county: '',
    eircode: '',
    phone: '',
    email: '',
    company_type: 'sole_trader',
    ryftpay_account_id: ''
  });
  const [studioInfoSaving, setStudioInfoSaving] = useState(false);

  // Irish counties
  const IRISH_COUNTIES = [
    'Antrim', 'Armagh', 'Carlow', 'Cavan', 'Clare', 'Cork', 'Derry', 'Donegal',
    'Down', 'Dublin', 'Fermanagh', 'Galway', 'Kerry', 'Kildare', 'Kilkenny',
    'Laois', 'Leitrim', 'Limerick', 'Longford', 'Louth', 'Mayo', 'Meath',
    'Monaghan', 'Offaly', 'Roscommon', 'Sligo', 'Tipperary', 'Tyrone',
    'Waterford', 'Westmeath', 'Wexford', 'Wicklow'
  ];

  // Auto-fetch WhatsApp templates once WhatsApp is enabled and the user is on the Brevo tab.
  useEffect(() => {
    if (brevoSettings.whatsapp_enabled && activeTab === 'brevo') {
      fetchWhatsappTemplates();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brevoSettings.whatsapp_enabled, activeTab]);

  // Handle return from RyftPay onboarding - auto-populate account ID
  const handleOnboardingReturn = async () => {
    try {
      // Fetch fresh onboarding status
      const statusRes = await apiGet('/api/payments/onboarding/status');
      setOnboardingStatus(statusRes);
      
      // If we have a real (non-mock) account ID, auto-save it to the studio
      if (statusRes.sub_account_id && !statusRes.sub_account_id.startsWith('ac_mock_') && !statusRes.sub_account_id.startsWith('sa_mock_')) {
        const accountId = statusRes.sub_account_id;
        
        // Update local state
        setStudioInfo(prev => ({ ...prev, ryftpay_account_id: accountId }));
        
        // Auto-save to studio record
        try {
          await apiPut('/api/studios/me', { body: { ryftpay_account_id: accountId } });
          toast.success(`RyftPay Account ID saved: ${accountId.substring(0, 20)}...`);
        } catch (saveErr) {
          console.error('Failed to auto-save account ID:', saveErr);
          // Still show the ID even if save failed
          toast.info(`Account ID: ${accountId} - Please save manually if needed`);
        }
      }
    } catch (err) {
      console.error('Failed to refresh onboarding status:', err);
    }
  };

  const fetchData = useCallback(async () => {
    try {
      // Bug #4 fix: catch /studios/me 404 individually so a transient
      // ownership mismatch (e.g. right after studio creation, before the
      // user JWT/local state syncs) doesn't nuke the whole settings page
      // with a generic "Failed to load settings" toast.
      const [studioRes, onboardingRes, brevoRes, cancellationRes, affiliateRes, templatesRes, widgetRes, planFeaturesRes] = await Promise.all([
        apiGet('/api/studios/me').catch((err) => {
          if (err?.status === 404) {
            return null;
          }
          throw err;
        }),
        apiGet('/api/payments/onboarding/status').catch(() => ({ status: 'not_started' })),
        apiGet('/api/studio/brevo-settings').catch(() => ({ api_key_set: false, enabled: false })),
        apiGet('/api/studio/cancellation-settings').catch(() => ({ min_notice_hours: 24, refund_type: 'class_pass_credit' })),
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/brevo/affiliate-link`).then(r => r.json()).catch(() => ({ affiliate_link: 'https://www.brevo.com' })),
        apiGet('/api/studio/message-templates').catch(() => ({ templates: {}, defaults: {}, variables: {}, event_types: [], channels: [] })),
        apiGet('/api/studio/widget-settings').catch(() => ({})),
        apiGet('/api/studio/plan-features').catch(() => ({ has_subscription: false, features: {}, plan_name: null }))
      ]);

      if (!studioRes) {
        // Studio not yet linked to this user — do not spam the "Failed to
        // load settings" toast. The parent dashboard will render a
        // create-studio prompt; we just release the loading spinner.
        setStudio(null);
        return;
      }

      setStudio(studioRes);
      setOnboardingStatus(onboardingRes);
      setBrevoSettings({
        api_key: '',
        sender_email: brevoRes.sender_email || '',
        sender_name: brevoRes.sender_name || '',
        enabled: brevoRes.enabled || false,
        api_key_set: brevoRes.api_key_set || false,
        sms_enabled: brevoRes.sms_enabled || false,
        sms_sender: brevoRes.sms_sender || '',
        whatsapp_enabled: brevoRes.whatsapp_enabled || false,
        whatsapp_sender: brevoRes.whatsapp_sender || ''
      });
      setCancellationSettings(cancellationRes);
      setBrevoAffiliateLink(affiliateRes.affiliate_link || 'https://www.brevo.com');
      
      // Set plan features
      if (planFeaturesRes) {
        setPlanFeatures(planFeaturesRes.features || {});
        setPlanName(planFeaturesRes.plan_name);
        setHasSubscription(planFeaturesRes.has_subscription);
      }
      
      // Set message templates data
      setTemplates(templatesRes.templates || {});
      setDefaults(templatesRes.defaults || {});
      setVariables(templatesRes.variables || {});
      setEventTypes(templatesRes.event_types || []);
      setChannels(templatesRes.channels || []);
      
      // Set widget settings
      if (widgetRes) {
        setWidgetSettings({
          enabled: widgetRes.enabled || false,
          background_color: widgetRes.background_color || '#ffffff',
          text_color: widgetRes.text_color || '#1f2937',
          button_color: widgetRes.button_color || '#3b82f6',
          button_text_color: widgetRes.button_text_color || '#ffffff',
          accent_color: widgetRes.accent_color || '#10b981',
          font_family: widgetRes.font_family || 'system-ui',
          border_radius: widgetRes.border_radius || '8px',
          show_instructor: widgetRes.show_instructor !== false,
          show_spots_remaining: widgetRes.show_spots_remaining !== false,
          show_activity_type: widgetRes.show_activity_type !== false
        });
        setEmbedCode(widgetRes.embed_code || '');
        setWidgetUrl(widgetRes.widget_url || '');
      }
      
      // Populate studio info form
      setStudioInfo({
        name: studioRes.name || '',
        description: studioRes.description || '',
        address: studioRes.address || '',
        city: studioRes.city || '',
        county: studioRes.county || '',
        eircode: studioRes.eircode || '',
        phone: studioRes.phone || '',
        email: studioRes.email || '',
        company_type: studioRes.company_type || 'sole_trader',
        ryftpay_account_id: studioRes.ryftpay_account_id || ''
      });
      
      // Fetch reviews
      if (studioRes?.id) {
        fetchReviews(studioRes.id);
      }
    } catch (e) {
      console.error('Failed to fetch settings:', e);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, []);

  // Iter-46an: effects below must come AFTER `fetchData` is declared with
  // `useCallback` — placing them before the const would hit a Temporal
  // Dead Zone when React evaluates the dependency array on first render.
  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle return from RyftPay onboarding (URL param ?onboarding=complete)
  useEffect(() => {
    if (searchParams.get('onboarding') === 'complete') {
      toast.success('RyftPay onboarding completed! Refreshing status...');
      handleOnboardingReturn();
      const next = new URLSearchParams(searchParams);
      next.delete('onboarding');
      setSearchParams(next);
      setActiveTab('payments');
    }

    const tabParam = searchParams.get('tab');
    if (tabParam) {
      setActiveTab(tabParam);
    }
    // handleOnboardingReturn is stable in this file — intentionally omitted
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams]);

  const fetchReviews = async (studioId) => {
    setReviewsLoading(true);
    try {
      const data = await apiGet('/api/reviews/studio/{studio_id}/full', { params: { studio_id: studioId } });
      setReviews(data);
    } catch (e) {
      console.error('Failed to fetch reviews:', e);
    } finally {
      setReviewsLoading(false);
    }
  };

  const handleAddActivityType = async () => {
    if (!newActivityType.trim()) return;
    const newType = newActivityType.trim().toLowerCase();
    if (studio?.activity_types?.includes(newType)) {
      toast.error('Activity type already exists');
      return;
    }
    setActivityTypeSaving(true);
    try {
      const updatedTypes = [...(studio?.activity_types || []), newType];
      await apiPut('/api/studios/me', { body: { activity_types: updatedTypes } });
      setStudio({ ...studio, activity_types: updatedTypes });
      setNewActivityType('');
      toast.success('Activity type added!');
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to add activity type'));
    } finally {
      setActivityTypeSaving(false);
    }
  };

  const handleRemoveActivityType = async (typeToRemove) => {
    if (!window.confirm(`Remove "${typeToRemove}"? Existing ClassPasses and classes with this type won't be affected.`)) return;
    setActivityTypeSaving(true);
    try {
      const updatedTypes = (studio?.activity_types || []).filter(t => t !== typeToRemove);
      await apiPut('/api/studios/me', { body: { activity_types: updatedTypes } });
      setStudio({ ...studio, activity_types: updatedTypes });
      toast.success('Activity type removed');
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to remove activity type'));
    } finally {
      setActivityTypeSaving(false);
    }
  };

  const handleSaveStudioInfo = async () => {
    if (!studioInfo.name.trim()) {
      toast.error('Studio name is required');
      return;
    }
    setStudioInfoSaving(true);
    try {
      await apiPut('/api/studios/me', { body: studioInfo });
      toast.success('Studio information updated!');
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to update studio information'));
    } finally {
      setStudioInfoSaving(false);
    }
  };

  // Iter-72: pending onboarding URL — surfaced as a clickable fallback
  // link inside the RyftPay card in case any programmatic redirect fails
  // (popup blocker, network flake, etc). Cleared once the user actually
  // clicks it or leaves the page.
  const [pendingOnboardingUrl, setPendingOnboardingUrl] = useState(null);

  // Iter-72: manual "Reconnect Existing Ryft Account" recovery flow. Studio
  // owners who see an ac_ ID from a prior failed attempt can paste it here
  // to adopt it into our DB, bypassing the failed 409-lookup path.
  const [showReconnect, setShowReconnect] = useState(false);
  const [reconnectAccountId, setReconnectAccountId] = useState('');
  const [reconnecting, setReconnecting] = useState(false);

  const handleReconnectRyft = async () => {
    const id = (reconnectAccountId || '').trim();
    if (!id.startsWith('ac_')) {
      toast.error("Ryft account IDs start with 'ac_'. Please double-check the value.");
      return;
    }
    setReconnecting(true);
    try {
      const res = await apiPost('/api/payments/onboarding/reconnect', {
        body: { ryft_account_id: id },
      });
      toast.success(res?.message || 'Reconnected successfully.');
      setShowReconnect(false);
      setReconnectAccountId('');
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to reconnect account'));
    } finally {
      setReconnecting(false);
    }
  };

  const handleStartOnboarding = async () => {
    const alreadyStarted = onboardingStatus && onboardingStatus.status !== 'not_started';

    setSaving(true);
    try {
      // Only create the sub-account on the first attempt. If one already
      // exists Ryft has the record and we just need a fresh KYC link.
      if (!alreadyStarted) {
        await apiPost('/api/payments/onboarding/create-sub-account', { body: {
          business_name: studio.name,
          business_type: 'individual',
          email: user.email,
          first_name: user.first_name,
          last_name: user.last_name
        } });
      }

      // Iter-72: ALWAYS try generate-link on "Continue Onboarding" —
      // previously we opened the Ryft *dashboard* (login page), but new
      // owners never set a password because they were popup-blocked out
      // of the hosted onboarding on the first attempt. generate-link
      // gives them the correct URL to finish KYC.
      let data;
      try {
        data = await apiPost('/api/payments/onboarding/generate-link');
      } catch (linkErr) {
        // 409 from backend = account already fully verified. In that case
        // the dashboard IS the right destination (owner has completed
        // onboarding and set their password via Ryft's flow).
        if (linkErr?.status === 409 || linkErr?.response?.status === 409) {
          handleLoginToRyftPay();
          return;
        }
        throw linkErr;
      }

      // Iter-72 fix: same-tab navigation instead of window.open(_blank),
      // which was silently popup-blocked after the awaits (root cause of
      // the "account appears but no redirect" bug).
      setPendingOnboardingUrl(data.url);
      toast.success('Redirecting to RyftPay to complete verification…');
      setTimeout(() => {
        window.location.assign(data.url);
      }, 250);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to start onboarding'));
    } finally {
      setSaving(false);
    }
  };

  // Open RyftPay dashboard for completed onboarding (or any account that
  // is already registered with Ryft).
  const handleLoginToRyftPay = () => {
    // Iter-46an: backend now tells us the correct dashboard URL based on
    // the platform's Ryft mode (sandbox vs live). Fallback to sandbox if
    // the field isn't present (e.g. older cached status response).
    const dashboardUrl =
      onboardingStatus?.dashboard_url || 'https://sandbox-dash.ryftpay.com/';

    // Iter-72: popup blockers (browser settings, ad-block extensions,
    // corporate policy) can silently block window.open, leaving the
    // studio owner staring at nothing. Detect the block and surface the
    // URL as a clickable fallback so a plain user-gesture <a> click can
    // still complete the trip.
    const win = window.open(dashboardUrl, '_blank', 'noopener,noreferrer');
    if (!win || win.closed || typeof win.closed === 'undefined') {
      setPendingOnboardingUrl(dashboardUrl);
      toast.info('Your browser blocked the popup — use the link below to open RyftPay.');
      return;
    }
    toast.success('Opening RyftPay Dashboard...');
  };

  const handleResetOnboarding = async () => {
    if (!window.confirm('Are you sure you want to reset your payment onboarding? This will delete your current sub-account.')) {
      return;
    }
    
    setSaving(true);
    try {
      await apiDelete('/api/payments/onboarding/reset');
      toast.success('Onboarding reset! You can now start fresh.');
      // Clear the account ID
      setStudioInfo(prev => ({ ...prev, ryftpay_account_id: '' }));
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to reset onboarding'));
    } finally {
      setSaving(false);
    }
  };

  // Refresh onboarding status and auto-fill account ID if available
  const handleRefreshOnboardingStatus = async () => {
    try {
      const statusRes = await apiGet('/api/payments/onboarding/status');
      setOnboardingStatus(statusRes);
      
      // Auto-populate account ID if we have a real (non-mock) account
      if (statusRes.sub_account_id && 
          !statusRes.sub_account_id.startsWith('ac_mock_') && 
          !statusRes.sub_account_id.startsWith('sa_mock_')) {
        
        const accountId = statusRes.sub_account_id;
        
        // Only update if different from current
        if (accountId !== studioInfo.ryftpay_account_id) {
          setStudioInfo(prev => ({ ...prev, ryftpay_account_id: accountId }));
          toast.success(`Account ID updated: ${accountId.substring(0, 25)}...`);
        } else {
          toast.success('Status refreshed');
        }
      } else {
        toast.success('Status refreshed');
      }
    } catch (err) {
      console.error('Failed to refresh status:', err);
      toast.error('Failed to refresh status');
    }
  };

  const handleSaveBrevoSettings = async () => {
    if (!brevoSettings.api_key && !brevoSettings.api_key_set) {
      toast.error('Please enter your Brevo API key');
      return;
    }
    setBrevoLoading(true);
    try {
      await apiPut('/api/studio/brevo-settings', { body: {
        api_key: brevoSettings.api_key || 'existing',
        sender_email: brevoSettings.sender_email,
        sender_name: brevoSettings.sender_name,
        enabled: brevoSettings.enabled
      } });
      toast.success('Brevo settings saved!');
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save Brevo settings'));
    } finally {
      setBrevoLoading(false);
    }
  };

  const handleSaveCancellationSettings = async () => {
    setSaving(true);
    try {
      await apiPut('/api/studio/cancellation-settings', { body: cancellationSettings });
      toast.success('Cancellation settings saved!');
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save settings'));
    } finally {
      setSaving(false);
    }
  };

  // WhatsApp template management
  const fetchWhatsappTemplates = async () => {
    setWhatsappTemplatesLoading(true);
    try {
      const data = await apiGet('/api/studio/whatsapp-templates');
      setWhatsappTemplates(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = formatApiError(e, 'Failed to load WhatsApp templates');
      toast.error(msg);
    } finally {
      setWhatsappTemplatesLoading(false);
    }
  };

  const handleSubmitWhatsappTemplate = async () => {
    if (!whatsappForm.template_name.trim() || !whatsappForm.template_content.trim()) {
      toast.error('Template name and content are required');
      return;
    }
    if (!/^[a-z0-9_]+$/.test(whatsappForm.template_name)) {
      toast.error('Template name must contain only lowercase letters, numbers, and underscores');
      return;
    }
    setWhatsappSubmitting(true);
    try {
      await apiPost('/api/studio/whatsapp-templates/submit', { body: whatsappForm });
      toast.success('Template submitted to Brevo for approval. Review typically takes 1–2 business days.');
      setWhatsappDialogOpen(false);
      setWhatsappForm({ template_name: '', language: 'en', template_content: '' });
      fetchWhatsappTemplates();
    } catch (e) {
      const msg = formatApiError(e, 'Failed to submit template');
      toast.error(msg);
    } finally {
      setWhatsappSubmitting(false);
    }
  };

  // Message Template handlers
  const getTemplateForChannel = (eventType, channel) => {
    const custom = templates[eventType]?.[channel];
    if (custom) return { ...custom, isCustom: true };
    const defaultTemplate = defaults[eventType]?.[channel];
    if (defaultTemplate) return { ...defaultTemplate, isCustom: false };
    return null;
  };

  const handleEditTemplate = (eventType, channel) => {
    const template = getTemplateForChannel(eventType, channel);
    setEditForm({
      subject: template?.subject || '',
      content: template?.content || '',
      is_active: template?.is_active !== false
    });
    setEditingChannel({ eventType, channel });
  };

  const handleSaveTemplate = async () => {
    if (!editingChannel) return;
    setSaving(true);
    try {
      await apiPost('/api/studio/message-templates', { body: {
        event_type: editingChannel.eventType,
        channel: editingChannel.channel,
        subject: editForm.subject,
        content: editForm.content,
        is_active: editForm.is_active
      } });
      toast.success('Template saved');
      await fetchData();
      setEditingChannel(null);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save template'));
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = async () => {
    if (!editingChannel) return;
    const customTemplate = templates[editingChannel.eventType]?.[editingChannel.channel];
    if (!customTemplate?.id) {
      toast.info('Already using default template');
      return;
    }
    if (!window.confirm('Reset to default template? Your customisations will be lost.')) return;
    setSaving(true);
    try {
      await apiDelete('/api/studio/message-templates/{template_id}', { params: { template_id: customTemplate.id } });
      toast.success('Reset to default template');
      await fetchData();
      setEditingChannel(null);
    } catch (e) {
      toast.error('Failed to reset template');
    } finally {
      setSaving(false);
    }
  };

  const handleTestTemplate = async () => {
    if (!testingTemplate) return;
    const customTemplate = templates[testingTemplate.eventType]?.[testingTemplate.channel];
    if (!customTemplate?.id) {
      toast.error('Please save the template first before testing');
      return;
    }
    setTestLoading(true);
    try {
      const data = await apiPost('/api/studio/message-templates/{template_id}/test', { params: { template_id: customTemplate.id } });
      toast.success(data.message || 'Test sent successfully');
      setShowTestDialog(false);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to send test'));
    } finally {
      setTestLoading(false);
    }
  };

  const insertVariable = (variable) => {
    const placeholder = `{{${variable}}}`;
    setEditForm(prev => ({ ...prev, content: prev.content + placeholder }));
    toast.success(`Added ${placeholder}`);
  };

  // Widget settings handlers
  const handleSaveWidgetSettings = async () => {
    setWidgetLoading(true);
    try {
      await apiPut('/api/studio/widget-settings', { body: widgetSettings });
      toast.success('Widget settings saved!');
      await fetchData();
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to save widget settings'));
    } finally {
      setWidgetLoading(false);
    }
  };

  const handleCopyEmbedCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Copied to clipboard!');
  };

  // Generate JavaScript snippet for the widget
  const generateJsSnippet = () => {
    if (!studio?.id) return '';
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    // Iter-46ai: escape every interpolated widget setting before splicing it
    // into the generated <script>. Studio admins choose their own widget
    // colours/fonts but a savvy admin could otherwise sneak HTML/JS into a
    // setting and have it execute on every site that embeds this snippet.
    // Defensive escape — quotes, backslashes, angle brackets, line breaks.
    const safe = (v) =>
      String(v ?? '')
        .replace(/\\/g, '\\\\')
        .replace(/'/g, "\\'")
        .replace(/</g, '\\u003c')
        .replace(/>/g, '\\u003e')
        .replace(/\r?\n/g, ' ');
    const buttonColor = safe(widgetSettings.button_color);
    const buttonTextColor = safe(widgetSettings.button_text_color);
    const borderRadius = safe(widgetSettings.border_radius);
    const fontFamily = safe(widgetSettings.font_family);
    const backgroundColor = safe(widgetSettings.background_color);
    const textColor = safe(widgetSettings.text_color);
    const safeStudioId = safe(studio.id);
    return `<script>
(function() {
  var btn = document.createElement('button');
  btn.id = 'studio-scheduler-btn';
  btn.textContent = 'Book a Class';
  btn.style.cssText = 'position:fixed;bottom:20px;right:20px;padding:12px 24px;background:${buttonColor};color:${buttonTextColor};border:none;border-radius:${borderRadius};cursor:pointer;font-family:${fontFamily};font-size:16px;font-weight:600;box-shadow:0 4px 12px rgba(0,0,0,0.15);z-index:9999;transition:transform 0.2s;';
  btn.onmouseover = function() { this.style.transform = 'scale(1.05)'; };
  btn.onmouseout = function() { this.style.transform = 'scale(1)'; };

  var modal = document.createElement('div');
  modal.id = 'studio-scheduler-modal';
  modal.style.cssText = 'display:none;position:fixed;top:0;left:0;width:100%;height:100%;background:rgba(0,0,0,0.5);z-index:10000;justify-content:center;align-items:center;';

  // Build the modal contents via DOM APIs instead of innerHTML so widget
  // settings can't escape the string context.
  var inner = document.createElement('div');
  inner.style.cssText = 'background:${backgroundColor};border-radius:12px;width:90%;max-width:800px;height:80%;max-height:700px;position:relative;overflow:hidden;';
  var closeBtn = document.createElement('button');
  closeBtn.textContent = '\u00d7';
  closeBtn.style.cssText = 'position:absolute;top:10px;right:10px;background:none;border:none;font-size:24px;cursor:pointer;color:${textColor};z-index:1;';
  closeBtn.onclick = function() { modal.style.display = 'none'; };
  var iframe = document.createElement('iframe');
  iframe.src = '${backendUrl}/widget/${safeStudioId}';
  iframe.style.cssText = 'width:100%;height:100%;border:none;';
  inner.appendChild(closeBtn);
  inner.appendChild(iframe);
  modal.appendChild(inner);

  btn.onclick = function() { modal.style.display = 'flex'; };
  modal.onclick = function(e) { if(e.target === modal) modal.style.display = 'none'; };

  document.body.appendChild(btn);
  document.body.appendChild(modal);
})();
</script>`;
  };

  const handlePhotoUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSaving(true);
    try {
      // Bug R20 fix (Iter-68): compress before upload so the multipart
      // request never approaches the ingress 1MB proxy-body-size ceiling.
      let uploadFile = file;
      try {
        uploadFile = await compressImage(file);
      } catch (compressErr) {
        // Fall back to the raw file — backend still enforces 5MB cap.
        console.warn('image compress failed, sending original:', compressErr);
      }
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('entity_type', type);
      formData.append('entity_id', studio.id);

      await apiPost('/api/upload/image', { body: formData });

      toast.success('Photo uploaded!');
      fetchData();
    } catch (e) {
      // Iter-68: friendlier message when the ingress rejects the upload.
      if (e?.status === 413) {
        toast.error(
          'That image is too large for upload. Try one under 2MB or a smaller resolution.'
        );
      } else {
        toast.error(formatApiError(e, 'Failed to upload photo'));
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="studio-settings-page">
        <div>
          <h1 className="text-3xl font-bold mb-2">Settings</h1>
          <p className="text-muted-foreground">Manage your studio settings and integrations</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="flex-wrap h-auto gap-1">
            <TabsTrigger value="studio">Studio Info</TabsTrigger>
            <TabsTrigger value="activity-types">Activity Types</TabsTrigger>
            <TabsTrigger value="photos">Photos</TabsTrigger>
            <TabsTrigger value="payments">Payments</TabsTrigger>
            <TabsTrigger value="brevo">Brevo</TabsTrigger>
            <TabsTrigger value="messages">Messages</TabsTrigger>
            <TabsTrigger value="widget">Website Widget</TabsTrigger>
            <TabsTrigger value="reviews">Reviews</TabsTrigger>
            <TabsTrigger value="cancellation">Cancellation</TabsTrigger>
          </TabsList>

          {/* Studio Information Tab */}
          <TabsContent value="studio">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Studio Information
                </CardTitle>
                <CardDescription>
                  Update your studio's basic information. This information is used for RyftPay onboarding.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="studio-name">Studio Name *</Label>
                  <Input
                    id="studio-name"
                    value={studioInfo.name}
                    onChange={(e) => setStudioInfo({ ...studioInfo, name: e.target.value })}
                    placeholder="Your Studio Name"
                    data-testid="studio-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="studio-description">Description</Label>
                  <textarea
                    id="studio-description"
                    value={studioInfo.description}
                    onChange={(e) => setStudioInfo({ ...studioInfo, description: e.target.value })}
                    placeholder="Describe your studio..."
                    className="w-full min-h-[100px] px-3 py-2 border rounded-md bg-background text-sm"
                    data-testid="studio-description-input"
                  />
                </div>

                {/* Company Type */}
                <div className="space-y-2">
                  <Label htmlFor="company-type">Company Type *</Label>
                  <Select 
                    value={studioInfo.company_type} 
                    onValueChange={(v) => setStudioInfo({ ...studioInfo, company_type: v })}
                  >
                    <SelectTrigger data-testid="company-type-select">
                      <SelectValue placeholder="Select company type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sole_trader">Sole Trader</SelectItem>
                      <SelectItem value="registered_company">Registered Company</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    This determines your business type for payment processing
                  </p>
                </div>

                {/* Address Section */}
                <div className="p-4 rounded-lg border bg-muted/30 space-y-4">
                  <h4 className="font-medium">Studio Address</h4>
                  
                  <div className="space-y-2">
                    <Label htmlFor="studio-address">Street Address</Label>
                    <Input
                      id="studio-address"
                      value={studioInfo.address}
                      onChange={(e) => setStudioInfo({ ...studioInfo, address: e.target.value })}
                      placeholder="123 Main Street"
                      data-testid="studio-address-input"
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="studio-city">City</Label>
                      <Input
                        id="studio-city"
                        value={studioInfo.city}
                        onChange={(e) => setStudioInfo({ ...studioInfo, city: e.target.value })}
                        placeholder="Dublin"
                        data-testid="studio-city-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="studio-county">County</Label>
                      <Select 
                        value={studioInfo.county} 
                        onValueChange={(v) => setStudioInfo({ ...studioInfo, county: v })}
                      >
                        <SelectTrigger data-testid="studio-county-select">
                          <SelectValue placeholder="Select county" />
                        </SelectTrigger>
                        <SelectContent>
                          {IRISH_COUNTIES.map((county) => (
                            <SelectItem key={county} value={county}>{county}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="studio-eircode">Eircode</Label>
                    <Input
                      id="studio-eircode"
                      value={studioInfo.eircode}
                      onChange={(e) => setStudioInfo({ ...studioInfo, eircode: e.target.value.toUpperCase() })}
                      placeholder="D01 F5P2"
                      maxLength={8}
                      data-testid="studio-eircode-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Irish postal code (e.g., D01 F5P2)
                    </p>
                  </div>
                </div>

                {/* Contact Section */}
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="studio-phone">Phone</Label>
                    <Input
                      id="studio-phone"
                      value={studioInfo.phone}
                      onChange={(e) => setStudioInfo({ ...studioInfo, phone: e.target.value })}
                      placeholder="+353 1 234 5678"
                      data-testid="studio-phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="studio-email">Email</Label>
                    <Input
                      id="studio-email"
                      type="email"
                      value={studioInfo.email}
                      onChange={(e) => setStudioInfo({ ...studioInfo, email: e.target.value })}
                      placeholder="info@yourstudio.ie"
                      data-testid="studio-email-input"
                    />
                  </div>
                </div>

                <Button 
                  onClick={handleSaveStudioInfo} 
                  disabled={studioInfoSaving}
                  data-testid="save-studio-info-btn"
                >
                  {studioInfoSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save Changes
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payment Processing Tab */}
          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  RyftPay Onboarding
                </CardTitle>
                <CardDescription>
                  Complete your payment setup to accept payments from clients
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* RyftPay Account ID Field */}
                <div className="space-y-2">
                  <Label htmlFor="ryftpay-account-id">RyftPay Account ID</Label>
                  <Input
                    id="ryftpay-account-id"
                    value={studioInfo.ryftpay_account_id}
                    onChange={(e) => setStudioInfo({ ...studioInfo, ryftpay_account_id: e.target.value })}
                    placeholder="ac_xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    data-testid="ryftpay-account-id-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Your RyftPay Account ID. This is auto-filled after completing onboarding or can be found in your RyftPay dashboard.
                  </p>
                </div>

                <Button 
                  onClick={handleSaveStudioInfo} 
                  disabled={studioInfoSaving}
                  variant="outline"
                  size="sm"
                  data-testid="save-ryftpay-account-btn"
                >
                  {studioInfoSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save Account ID
                </Button>

                <hr className="my-4" />

                <div className="p-4 rounded-lg bg-muted">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Onboarding Status</p>
                      <p className="text-sm text-muted-foreground">
                        {onboardingStatus?.status === 'not_started' && 'Not started - Click below to begin'}
                        {onboardingStatus?.status === 'mock_created' && (
                          onboardingStatus?.can_upgrade 
                            ? 'Mock account - Platform API key is now available. Reset to use real RyftPay.' 
                            : 'Account created (sandbox mode - no platform API key configured)'
                        )}
                        {onboardingStatus?.status === 'api_key_invalid' && 'The platform RyftPay API key is invalid. Please contact support.'}
                        {onboardingStatus?.status === 'created' && 'Account created - Complete verification'}
                        {onboardingStatus?.status === 'pending' && !onboardingStatus?.onboarding_complete && 'Account created - Complete onboarding in RyftPay portal'}
                        {(onboardingStatus?.status === 'active' || onboardingStatus?.onboarding_complete) && 'Account active and ready to accept payments'}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      {onboardingStatus?.is_mock && (
                        <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-300">
                          {onboardingStatus?.status === 'api_key_invalid' ? 'Invalid Key' : 'Mock'}
                        </Badge>
                      )}
                      <Badge 
                        variant={(onboardingStatus?.onboarding_complete || onboardingStatus?.can_accept_payments) ? 'default' : 'outline'}
                        className={(onboardingStatus?.onboarding_complete || onboardingStatus?.can_accept_payments) ? 'bg-emerald-500' : ''}
                      >
                        {(onboardingStatus?.onboarding_complete || onboardingStatus?.can_accept_payments) ? 'Active' :
                         onboardingStatus?.status === 'not_started' ? 'Not Started' : 'Pending'}
                      </Badge>
                    </div>
                  </div>
                </div>

                {/* API Key Invalid notice */}
                {onboardingStatus?.status === 'api_key_invalid' && (
                  <div className="p-4 rounded-lg border border-red-300 bg-red-50">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-red-800">RyftPay API Key Invalid</p>
                        <p className="text-sm text-red-700">
                          The platform's RyftPay API key is not working (403 Forbidden). 
                          Please contact the platform administrator to configure a valid API key.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Mock account upgrade notice - only show if API key is valid */}
                {onboardingStatus?.is_mock && onboardingStatus?.can_upgrade && onboardingStatus?.status !== 'api_key_invalid' && (
                  <div className="p-4 rounded-lg border border-amber-300 bg-amber-50">
                    <div className="flex items-start gap-3">
                      <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-medium text-amber-800">Upgrade Available</p>
                        <p className="text-sm text-amber-700">
                          Your account was created in mock mode, but the platform now has a valid RyftPay API key. 
                          Reset your onboarding to create a real RyftPay account.
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {onboardingStatus && onboardingStatus.status !== 'not_started' && (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-3 rounded-lg border">
                      <p className="text-sm text-muted-foreground">Business Name</p>
                      <p className="font-medium">{onboardingStatus.business_name || studio?.name}</p>
                    </div>
                    <div className="p-3 rounded-lg border">
                      <p className="text-sm text-muted-foreground">Can Accept Payments</p>
                      <p className="font-medium flex items-center gap-1">
                        {onboardingStatus.can_accept_payments || onboardingStatus.onboarding_complete ? (
                          <><CheckCircle className="w-4 h-4 text-emerald-500" /> Yes</>
                        ) : (
                          <><AlertCircle className="w-4 h-4 text-amber-500" /> {onboardingStatus?.is_mock ? 'Mock Mode' : 'Pending'}</>
                        )}
                      </p>
                    </div>
                    {/* Iter-72: expose Ryft's raw verification.status so
                        studio owners can see exactly what Ryft reports.
                        Helps them distinguish "Ryft says you're verified"
                        from "Ryft skipped KYC (NotRequired)". */}
                    {onboardingStatus.verification_status && (
                      <div className="p-3 rounded-lg border" data-testid="ryft-verification-status">
                        <p className="text-sm text-muted-foreground">Ryft Verification</p>
                        <p className="font-medium flex items-center gap-1">
                          {onboardingStatus.verification_status === 'Verified' && (
                            <><CheckCircle className="w-4 h-4 text-emerald-500" /> Verified</>
                          )}
                          {onboardingStatus.verification_status === 'NotRequired' && (
                            <><CheckCircle className="w-4 h-4 text-sky-500" /> Not Required</>
                          )}
                          {onboardingStatus.verification_status === 'Pending' && (
                            <><Loader2 className="w-4 h-4 animate-spin text-amber-500" /> Pending</>
                          )}
                          {onboardingStatus.verification_status === 'Required' && (
                            <><AlertCircle className="w-4 h-4 text-amber-500" /> KYC Required</>
                          )}
                          {!['Verified','NotRequired','Pending','Required'].includes(onboardingStatus.verification_status) && (
                            <>{onboardingStatus.verification_status}</>
                          )}
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Iter-72: if Ryft reports NotRequired we surface a hint
                    explaining what that means so owners aren't confused
                    about "why does it say Active when I never did KYC?" */}
                {onboardingStatus?.verification_status === 'NotRequired' && (
                  <div className="p-3 rounded-lg border border-sky-300 bg-sky-50 dark:bg-sky-950/20 text-sm text-sky-800 dark:text-sky-200" data-testid="verification-not-required-hint">
                    <p className="font-medium mb-1">Why is my account already active?</p>
                    <p>
                      RyftPay has flagged this account as <span className="font-mono">NotRequired</span> for verification — it can accept payments without a full KYC review. This can happen for test-tier accounts, certain business types, or when your merchant onboarding was completed via Ryft support directly. If you believe you need to finish a hosted-onboarding flow, click &ldquo;Log Into RyftPay Dashboard&rdquo; to review your account settings on Ryft&apos;s side.
                    </p>
                  </div>
                )}

                {/* Iter-72: manual fallback link in case the auto-redirect
                    doesn't fire (popup blockers, ad-block extensions, etc).
                    Shown for a few seconds around the redirect. */}
                {pendingOnboardingUrl && (
                  <div className="p-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20" data-testid="onboarding-manual-link">
                    <p className="text-sm text-amber-800 dark:text-amber-200 mb-2">
                      Not redirected automatically?
                    </p>
                    <a
                      href={pendingOnboardingUrl}
                      className="inline-flex items-center gap-1 text-sm font-medium text-primary underline hover:no-underline"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Click here to open the RyftPay verification page
                    </a>
                  </div>
                )}

                {/* Iter-72: manual recovery — paste an existing Ryft
                    account_id to reconnect. Solves the "I see an ac_...
                    ID from a previous attempt but our DB lost it" case. */}
                {showReconnect ? (
                  <div className="p-4 rounded-lg border bg-muted/40 space-y-3" data-testid="reconnect-ryft-card">
                    <div>
                      <p className="text-sm font-medium mb-1">Reconnect an existing RyftPay account</p>
                      <p className="text-xs text-muted-foreground">
                        If you already started onboarding with RyftPay in a previous session, paste your account ID below. It starts with <span className="font-mono">ac_</span> and can be found in your RyftPay account or by contacting RyftPay support.
                      </p>
                    </div>
                    <Input
                      value={reconnectAccountId}
                      onChange={(e) => setReconnectAccountId(e.target.value)}
                      placeholder="ac_xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                      className="font-mono text-sm"
                      data-testid="reconnect-account-id-input"
                    />
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={handleReconnectRyft}
                        disabled={reconnecting || !reconnectAccountId.trim()}
                        data-testid="reconnect-submit-btn"
                      >
                        {reconnecting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Reconnect
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setShowReconnect(false); setReconnectAccountId(''); }}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : null}

                <div className="flex gap-2 flex-wrap">
                  {onboardingStatus?.is_mock && onboardingStatus?.can_upgrade && onboardingStatus?.status !== 'api_key_invalid' ? (
                    <Button 
                      onClick={handleResetOnboarding}
                      disabled={saving}
                      variant="default"
                      data-testid="reset-onboarding-btn"
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                      Reset & Start Real Onboarding
                    </Button>
                  ) : onboardingStatus?.onboarding_complete || onboardingStatus?.can_accept_payments ? (
                    <Button 
                      onClick={handleLoginToRyftPay}
                      variant="default"
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="login-ryftpay-btn"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Log Into RyftPay Dashboard
                    </Button>
                  ) : (
                    <Button 
                      onClick={handleStartOnboarding}
                      disabled={saving || onboardingStatus?.is_mock}
                      data-testid="start-onboarding-btn"
                    >
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ExternalLink className="w-4 h-4 mr-2" />}
                      {onboardingStatus?.status === 'not_started' ? 'Start Onboarding' : 
                       onboardingStatus?.status === 'api_key_invalid' ? 'Invalid API Key' :
                       onboardingStatus?.is_mock ? 'Mock Mode (No API Key)' : 'Continue Verification'}
                    </Button>
                  )}
                  <Button variant="outline" onClick={handleRefreshOnboardingStatus}>Refresh Status</Button>
                  {/* Iter-72: reconnect-existing button always visible so
                      owners stuck in the 409 zombie state have an escape
                      hatch without needing to hit reset first. */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowReconnect((v) => !v)}
                    data-testid="show-reconnect-btn"
                  >
                    {showReconnect ? 'Hide reconnect' : 'Reconnect existing RyftPay account'}
                  </Button>
                  {onboardingStatus?.status !== 'not_started' && (
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      onClick={handleResetOnboarding}
                      disabled={saving}
                      className="text-destructive hover:text-destructive"
                      data-testid="reset-onboarding-secondary-btn"
                    >
                      Reset Onboarding
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Types Tab */}
          <TabsContent value="activity-types">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Tag className="w-5 h-5" />
                  Activity Types
                </CardTitle>
                <CardDescription>
                  Define the types of classes and activities your studio offers. ClassPasses are linked to these activity types.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-2">
                  <Input 
                    value={newActivityType}
                    onChange={(e) => setNewActivityType(e.target.value)}
                    placeholder="e.g., yoga, pilates, strength"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && newActivityType.trim()) {
                        handleAddActivityType();
                      }
                    }}
                    data-testid="new-activity-type-input"
                  />
                  <Button 
                    onClick={handleAddActivityType}
                    disabled={activityTypeSaving || !newActivityType.trim()}
                    data-testid="add-activity-type-btn"
                  >
                    {activityTypeSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2">
                  {(studio?.activity_types || []).map((type, i) => (
                    <Badge 
                      key={`activity-${type}`} 
                      variant="secondary" 
                      className="px-3 py-1.5 text-sm capitalize flex items-center gap-2"
                      data-testid={`activity-type-${i}`}
                    >
                      {type}
                      <button
                        onClick={() => handleRemoveActivityType(type)}
                        className="ml-1 hover:text-destructive"
                        data-testid={`remove-activity-type-${i}`}
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                  {(!studio?.activity_types || studio.activity_types.length === 0) && (
                    <p className="text-muted-foreground text-sm">No activity types defined yet</p>
                  )}
                </div>

                <p className="text-xs text-muted-foreground">
                  Tip: Activity types help organize your ClassPasses. Clients can only use a ClassPass for classes matching its activity type.
                </p>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Photos Tab */}
          <TabsContent value="photos">
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Cover Photo</CardTitle>
                  <CardDescription>Main banner image for your studio page</CardDescription>
                </CardHeader>
                <CardContent>
                  {studio?.cover_image_url ? (
                    <img 
                      src={studio.cover_image_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${studio.cover_image_url}` : studio.cover_image_url}
                      alt="Cover"
                      className="w-full h-40 object-cover rounded-lg mb-3"
                    />
                  ) : (
                    <div className="w-full h-40 bg-muted rounded-lg flex items-center justify-center mb-3">
                      <Image className="w-12 h-12 text-muted-foreground/50" />
                    </div>
                  )}
                  <div className="relative">
                    <input type="file" accept="image/*" onChange={(e) => handlePhotoUpload(e, 'studio_cover')} 
                      className="absolute inset-0 opacity-0 cursor-pointer" disabled={saving} />
                    <Button variant="outline" className="w-full" disabled={saving}>
                      <Upload className="w-4 h-4 mr-2" />Upload Cover Photo
                    </Button>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Studio Logo</CardTitle>
                  <CardDescription>Your studio's logo or icon</CardDescription>
                </CardHeader>
                <CardContent>
                  {studio?.logo_url ? (
                    <img 
                      src={studio.logo_url.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${studio.logo_url}` : studio.logo_url}
                      alt="Logo"
                      className="w-24 h-24 object-cover rounded-lg mb-3 mx-auto"
                    />
                  ) : (
                    <div className="w-24 h-24 bg-muted rounded-lg flex items-center justify-center mb-3 mx-auto">
                      <Image className="w-8 h-8 text-muted-foreground/50" />
                    </div>
                  )}
                  <div className="relative">
                    <input type="file" accept="image/*" onChange={(e) => handlePhotoUpload(e, 'studio_logo')} 
                      className="absolute inset-0 opacity-0 cursor-pointer" disabled={saving} />
                    <Button variant="outline" className="w-full" disabled={saving}>
                      <Upload className="w-4 h-4 mr-2" />Upload Logo
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card className="mt-4">
              <CardHeader>
                <CardTitle className="text-base">Gallery Photos</CardTitle>
                <CardDescription>Additional photos of your studio</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                  {(studio?.photos || []).map((photo, i) => (
                    <img 
                      key={`photo-${photo}`}
                      src={photo.startsWith('/api') ? `${process.env.REACT_APP_BACKEND_URL}${photo}` : photo}
                      alt={`Gallery ${i + 1}`}
                      className="w-full h-24 object-cover rounded-lg"
                    />
                  ))}
                </div>
                <div className="relative inline-block">
                  <input type="file" accept="image/*" onChange={(e) => handlePhotoUpload(e, 'studio')} 
                    className="absolute inset-0 opacity-0 cursor-pointer" disabled={saving} />
                  <Button variant="outline" disabled={saving}>
                    <Upload className="w-4 h-4 mr-2" />Add Gallery Photo
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Brevo Tab */}
          <TabsContent value="brevo">
            {!planFeatures.brevo_integration ? (
              <Card className="border-amber-200 bg-amber-50/50">
                <CardContent className="py-12 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                    <Lock className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Brevo Integration</h3>
                  <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                    Unlock SMS and WhatsApp messaging to send booking confirmations, reminders, and marketing messages to your clients.
                  </p>
                  <div className="flex items-center justify-center gap-2 mb-6">
                    <Badge variant="outline" className="text-amber-700 border-amber-300">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Premium Feature
                    </Badge>
                  </div>
                  <Button asChild>
                    <a href="/studio/subscription">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Upgrade Your Plan
                    </a>
                  </Button>
                  {planName && (
                    <p className="text-xs text-muted-foreground mt-4">
                      Current plan: {planName}
                    </p>
                  )}
                </CardContent>
              </Card>
            ) : (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Brevo Integration
                </CardTitle>
                <CardDescription>
                  Connect Brevo for email marketing, SMS, and WhatsApp messaging
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 rounded-lg border bg-blue-50 border-blue-200">
                  <p className="text-sm text-blue-800 mb-2">
                    Don't have a Brevo account? Sign up using our partner link:
                  </p>
                  <Button variant="outline" size="sm" asChild>
                    <a href={brevoAffiliateLink} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Sign up for Brevo
                    </a>
                  </Button>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Brevo API Key</Label>
                    <Input 
                      type="password"
                      value={brevoSettings.api_key}
                      onChange={(e) => setBrevoSettings({ ...brevoSettings, api_key: e.target.value })}
                      placeholder={brevoSettings.api_key_set ? "••••••••••••••••" : "xkeysib-..."}
                      data-testid="brevo-api-key-input"
                    />
                    {brevoSettings.api_key_set && (
                      <p className="text-xs text-emerald-600">API key is configured</p>
                    )}
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Sender Email</Label>
                      <Input 
                        type="email"
                        value={brevoSettings.sender_email}
                        onChange={(e) => setBrevoSettings({ ...brevoSettings, sender_email: e.target.value })}
                        placeholder="noreply@yourstudio.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Sender Name</Label>
                      <Input 
                        value={brevoSettings.sender_name}
                        onChange={(e) => setBrevoSettings({ ...brevoSettings, sender_name: e.target.value })}
                        placeholder={studio?.name || "Your Studio"}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg border">
                    <div>
                      <Label>Enable Brevo Integration</Label>
                      <p className="text-sm text-muted-foreground">
                        Allow sending marketing emails and notifications
                      </p>
                    </div>
                    <Switch 
                      checked={brevoSettings.enabled}
                      onCheckedChange={(checked) => setBrevoSettings({ ...brevoSettings, enabled: checked })}
                    />
                  </div>

                  {/* SMS Settings */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">SMS Settings</h4>
                    <div className="flex items-center justify-between p-4 rounded-lg border mb-3">
                      <div>
                        <Label>Enable SMS via Brevo</Label>
                        <p className="text-sm text-muted-foreground">
                          Send SMS notifications through Brevo
                        </p>
                      </div>
                      <Switch 
                        checked={brevoSettings.sms_enabled}
                        onCheckedChange={(checked) => setBrevoSettings({ ...brevoSettings, sms_enabled: checked })}
                      />
                    </div>
                    {brevoSettings.sms_enabled && (
                      <div className="space-y-2">
                        <Label>SMS Sender Name</Label>
                        <Input 
                          value={brevoSettings.sms_sender || ''}
                          onChange={(e) => setBrevoSettings({ ...brevoSettings, sms_sender: e.target.value })}
                          placeholder="YourStudio"
                          maxLength={11}
                        />
                        <p className="text-xs text-muted-foreground">Max 11 characters, letters and numbers only</p>
                      </div>
                    )}
                  </div>

                  {/* WhatsApp Settings */}
                  <div className="border-t pt-4">
                    <h4 className="font-medium mb-3">WhatsApp Settings</h4>
                    
                    {/* WhatsApp Instructions */}
                    <div className="p-4 rounded-lg border bg-amber-50 border-amber-200 mb-4">
                      <div className="flex items-start gap-3">
                        <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
                        <div className="text-sm text-amber-800">
                          <p className="font-medium mb-2">WhatsApp Business Setup Required</p>
                          <p className="mb-2">To send WhatsApp messages, you must:</p>
                          <ol className="list-decimal list-inside space-y-1 mb-2">
                            <li>Create a <a href="https://business.facebook.com" target="_blank" rel="noopener noreferrer" className="underline font-medium">Meta Business Account</a></li>
                            <li>Set up a WhatsApp Business account in Brevo</li>
                            <li>Submit your message templates for Meta approval</li>
                            <li>Complete Meta's business verification process</li>
                          </ol>
                          <p className="text-xs mt-2">
                            <strong>Note:</strong> Each studio must set up their own WhatsApp Business account. Template approval typically takes 1-2 business days.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-4 rounded-lg border mb-3">
                      <div>
                        <Label>Enable WhatsApp via Brevo</Label>
                        <p className="text-sm text-muted-foreground">
                          Send WhatsApp notifications through Brevo
                        </p>
                      </div>
                      <Switch 
                        checked={brevoSettings.whatsapp_enabled}
                        onCheckedChange={(checked) => setBrevoSettings({ ...brevoSettings, whatsapp_enabled: checked })}
                      />
                    </div>
                    {brevoSettings.whatsapp_enabled && (
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label>WhatsApp Business Number</Label>
                          <Input 
                            value={brevoSettings.whatsapp_sender || ''}
                            onChange={(e) => setBrevoSettings({ ...brevoSettings, whatsapp_sender: e.target.value })}
                            placeholder="+353851234567"
                          />
                          <p className="text-xs text-muted-foreground">Your verified WhatsApp Business number</p>
                        </div>
                        
                        <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                          <p className="text-sm text-blue-800">
                            <strong>Template Guide:</strong> WhatsApp requires pre-approved templates for business messages.
                            You can submit templates below or configure them directly in your <a href="https://app.brevo.com/whatsapp/templates" target="_blank" rel="noopener noreferrer" className="underline font-medium">Brevo WhatsApp Templates</a> section.
                          </p>
                        </div>

                        {/* WhatsApp Template Management */}
                        <Card className="border-dashed" data-testid="whatsapp-templates-card">
                          <CardHeader>
                            <div className="flex items-center justify-between">
                              <div>
                                <CardTitle className="text-base">WhatsApp Templates</CardTitle>
                                <CardDescription>
                                  Submit new templates to Meta (via Brevo) and track their approval status.
                                </CardDescription>
                              </div>
                              <div className="flex gap-2">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={fetchWhatsappTemplates}
                                  disabled={whatsappTemplatesLoading}
                                  data-testid="whatsapp-refresh-templates-btn"
                                >
                                  <RotateCcw className={`w-3.5 h-3.5 mr-1.5 ${whatsappTemplatesLoading ? 'animate-spin' : ''}`} />
                                  Refresh
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => setWhatsappDialogOpen(true)}
                                  data-testid="whatsapp-submit-template-btn"
                                >
                                  <Plus className="w-3.5 h-3.5 mr-1.5" />Submit new
                                </Button>
                              </div>
                            </div>
                          </CardHeader>
                          <CardContent>
                            {whatsappTemplatesLoading ? (
                              <div className="flex justify-center py-6">
                                <Loader2 className="w-5 h-5 animate-spin" />
                              </div>
                            ) : whatsappTemplates.length === 0 ? (
                              <div className="text-center py-6 text-sm text-muted-foreground" data-testid="whatsapp-templates-empty">
                                No templates submitted yet. Click "Submit new" to get started.
                              </div>
                            ) : (
                              <div className="space-y-2">
                                {whatsappTemplates.map((t, i) => (
                                  <div
                                    key={t.id || i}
                                    className="flex items-start justify-between p-3 border rounded-lg"
                                    data-testid={`whatsapp-template-row-${i}`}
                                  >
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2 mb-1">
                                        <code className="text-sm font-mono font-medium">{t.template_name}</code>
                                        <Badge
                                          variant={
                                            t.status === 'approved' ? 'default'
                                              : t.status === 'rejected' ? 'destructive'
                                              : 'secondary'
                                          }
                                          className="text-xs"
                                          data-testid={`whatsapp-template-status-${i}`}
                                        >
                                          {t.status || 'pending'}
                                        </Badge>
                                        {t.language && (
                                          <span className="text-xs text-muted-foreground uppercase">{t.language}</span>
                                        )}
                                      </div>
                                      <p className="text-sm text-muted-foreground truncate">
                                        {t.template_content || ''}
                                      </p>
                                      <p className="text-xs text-muted-foreground mt-1">
                                        {t.created_at ? `Submitted ${format(new Date(t.created_at), 'MMM d, yyyy')}` : ''}
                                        {t.rejection_reason ? ` — ${t.rejection_reason}` : ''}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </CardContent>
                        </Card>
                      </div>
                    )}
                  </div>
                </div>

                <Button onClick={handleSaveBrevoSettings} disabled={brevoLoading} data-testid="save-brevo-btn">
                  {brevoLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save Brevo Settings
                </Button>
              </CardContent>
            </Card>
            )}
          </TabsContent>

          {/* Reviews Tab */}
          <TabsContent value="reviews">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Star className="w-5 h-5" />
                  Client Reviews
                </CardTitle>
                <CardDescription>
                  View detailed feedback from your clients (ratings are public, comments are for you only)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {reviewsLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin" />
                  </div>
                ) : reviews.length > 0 ? (
                  <div className="space-y-4">
                    {reviews.map((review, i) => (
                      <div key={review.id} className="p-4 border rounded-lg" data-testid={`owner-review-${i}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-3">
                            <p className="font-medium">{review.user_name}</p>
                            <div className="flex gap-0.5">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <Star 
                                  key={star} 
                                  className={`w-4 h-4 ${star <= review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'}`}
                                />
                              ))}
                            </div>
                          </div>
                          <p className="text-sm text-muted-foreground">
                            {format(new Date(review.created_at), 'MMM d, yyyy')}
                          </p>
                        </div>
                        {review.comment && (
                          <p className="text-muted-foreground">{review.comment}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Star className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                    <p className="text-muted-foreground">No reviews yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Cancellation Policy Tab */}
          <TabsContent value="cancellation">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Cancellation Policy
                </CardTitle>
                <CardDescription>
                  Configure how automatic refunds are handled for cancellations
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Minimum Notice Hours</Label>
                  <Input 
                    type="number"
                    value={cancellationSettings.min_notice_hours}
                    onChange={(e) => setCancellationSettings({ 
                      ...cancellationSettings, 
                      min_notice_hours: parseInt(e.target.value) || 24 
                    })}
                    min={0}
                    data-testid="notice-hours-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Clients must cancel at least this many hours before class to receive a refund
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Refund Type</Label>
                  <Select 
                    value={cancellationSettings.refund_type}
                    onValueChange={(v) => setCancellationSettings({ ...cancellationSettings, refund_type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="class_pass_credit">ClassPass Credit Returned</SelectItem>
                      <SelectItem value="full_refund">Full Payment Refund</SelectItem>
                      <SelectItem value="no_refund">No Automatic Refund</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Late Cancellation Penalty (%)</Label>
                  <Input 
                    type="number"
                    value={cancellationSettings.late_cancel_penalty}
                    onChange={(e) => setCancellationSettings({ 
                      ...cancellationSettings, 
                      late_cancel_penalty: parseFloat(e.target.value) || 0 
                    })}
                    min={0}
                    max={100}
                  />
                  <p className="text-xs text-muted-foreground">
                    Percentage of class value charged for late cancellations (0 = no penalty)
                  </p>
                </div>

                {/* Iter-46al: per-studio waitlist priority window */}
                <div className="space-y-2">
                  <Label>Waitlist Priority Window</Label>
                  <Select
                    value={String(cancellationSettings.waitlist_grace_minutes ?? 60)}
                    onValueChange={(v) => setCancellationSettings({
                      ...cancellationSettings,
                      waitlist_grace_minutes: parseInt(v, 10),
                    })}
                  >
                    <SelectTrigger data-testid="waitlist-grace-select">
                      <SelectValue placeholder="Select duration" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">0 minutes (no priority — open to public immediately)</SelectItem>
                      {Array.from({ length: 8 }, (_, i) => (i + 1) * 30).map((m) => (
                        <SelectItem key={m} value={String(m)}>
                          {m < 60
                            ? `${m} minutes`
                            : m === 60
                              ? '1 hour'
                              : m % 60 === 0
                                ? `${m / 60} hours`
                                : `${Math.floor(m / 60)}h ${m % 60}m`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    When a client cancels, waitlisted members are notified. During this window only they can book the freed seat. After it expires the class re-opens to everyone. Set 0 to skip the window entirely — notifications still send, public can book immediately.
                  </p>
                </div>

                <Button onClick={handleSaveCancellationSettings} disabled={saving} data-testid="save-cancellation-btn">
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Save Cancellation Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <div className="space-y-6">
              {/* Info Card */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="flex items-start gap-3 pt-4">
                  <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">Available Variables</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(variables).map(([key, desc]) => (
                        <Badge key={key} variant="outline" className="text-xs bg-white" title={desc}>
                          {`{{${key}}}`}
                        </Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Event Type Tabs */}
              <Tabs value={selectedEvent} onValueChange={setSelectedEvent}>
                <TabsList className="flex flex-wrap h-auto gap-1">
                  {eventTypes.map(event => (
                    <TabsTrigger key={event} value={event} className="text-xs px-3 py-1.5">
                      {EVENT_TYPE_LABELS[event] || event}
                    </TabsTrigger>
                  ))}
                </TabsList>

                {eventTypes.map(eventType => (
                  <TabsContent key={eventType} value={eventType} className="mt-4">
                    <div className="grid md:grid-cols-3 gap-4">
                      {channels.map(channel => {
                        const template = getTemplateForChannel(eventType, channel);
                        const Icon = CHANNEL_ICONS[channel];
                        
                        return (
                          <Card key={channel} className="relative">
                            <CardHeader className="pb-3">
                              <div className="flex items-center justify-between">
                                <CardTitle className="text-base flex items-center gap-2">
                                  <Icon className="w-4 h-4" />
                                  {channel.charAt(0).toUpperCase() + channel.slice(1)}
                                </CardTitle>
                                <div className="flex items-center gap-2">
                                  {template?.isCustom && (
                                    <Badge variant="secondary" className="text-xs">Custom</Badge>
                                  )}
                                  {template?.is_active !== false ? (
                                    <Badge variant="default" className="text-xs bg-emerald-500">Active</Badge>
                                  ) : (
                                    <Badge variant="outline" className="text-xs">Disabled</Badge>
                                  )}
                                </div>
                              </div>
                              {channel === 'email' && template?.subject && (
                                <p className="text-sm text-muted-foreground truncate">
                                  Subject: {template.subject}
                                </p>
                              )}
                            </CardHeader>
                            <CardContent className="space-y-3">
                              <div className="bg-muted p-3 rounded text-xs font-mono max-h-32 overflow-y-auto whitespace-pre-wrap">
                                {template?.content || 'No template configured'}
                              </div>
                              <div className="flex gap-2">
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleEditTemplate(eventType, channel)}
                                  data-testid={`edit-${eventType}-${channel}-btn`}
                                >
                                  Edit
                                </Button>
                                {template?.isCustom && template?.id && (
                                  <Button 
                                    size="sm" 
                                    variant="ghost"
                                    onClick={() => {
                                      setTestingTemplate({ eventType, channel });
                                      setShowTestDialog(true);
                                    }}
                                    data-testid={`test-${eventType}-${channel}-btn`}
                                  >
                                    <TestTube2 className="w-3 h-3 mr-1" />
                                    Test
                                  </Button>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  </TabsContent>
                ))}
              </Tabs>

              {/* Custom Sender ID — Iter-46s */}
              <CustomSenderIdSection />
            </div>
          </TabsContent>

          {/* Website Widget Tab */}
          <TabsContent value="widget">
            {!planFeatures.widget_embedding ? (
              <Card className="border-amber-200 bg-amber-50/50">
                <CardContent className="py-12 text-center">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-100 flex items-center justify-center">
                    <Lock className="w-8 h-8 text-amber-600" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Website Widget</h3>
                  <p className="text-muted-foreground mb-4 max-w-md mx-auto">
                    Embed a booking calendar widget on your own website to allow clients to book classes directly.
                  </p>
                  <div className="flex items-center justify-center gap-2 mb-6">
                    <Badge variant="outline" className="text-amber-700 border-amber-300">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Premium Feature
                    </Badge>
                  </div>
                  <Button asChild>
                    <a href="/studio/subscription">
                      <Sparkles className="w-4 h-4 mr-2" />
                      Upgrade Your Plan
                    </a>
                  </Button>
                  {planName && (
                    <p className="text-xs text-muted-foreground mt-4">
                      Current plan: {planName}
                    </p>
                  )}
                </CardContent>
              </Card>
            ) : (
            <div className="space-y-6 max-w-4xl">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Website Widget
                  </CardTitle>
                  <CardDescription>
                    Add a booking button to your website that opens a class calendar modal
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Enable Widget */}
                  <div className="flex items-center justify-between p-4 rounded-lg border">
                    <div className="flex-1 min-w-0 mr-4">
                      <Label>Enable Website Widget</Label>
                      <p className="text-sm text-muted-foreground">
                        Allow your class calendar to be embedded on external websites
                      </p>
                    </div>
                    <Switch 
                      checked={widgetSettings.enabled}
                      onCheckedChange={(checked) => setWidgetSettings(prev => ({ ...prev, enabled: checked }))}
                      data-testid="widget-enabled-switch"
                    />
                  </div>

                  {widgetSettings.enabled && (
                    <>
                      {/* Colour Customisation */}
                      <div className="space-y-4">
                        <h4 className="font-medium">Customise Appearance</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label>Button Colour</Label>
                            <div className="flex gap-2">
                              <Input
                                type="color"
                                value={widgetSettings.button_color}
                                onChange={(e) => setWidgetSettings(prev => ({ ...prev, button_color: e.target.value }))}
                                className="w-12 h-10 p-1 cursor-pointer flex-shrink-0"
                              />
                              <Input
                                value={widgetSettings.button_color}
                                onChange={(e) => setWidgetSettings(prev => ({ ...prev, button_color: e.target.value }))}
                                className="flex-1 min-w-0"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Button Text Colour</Label>
                            <div className="flex gap-2">
                              <Input
                                type="color"
                                value={widgetSettings.button_text_color}
                                onChange={(e) => setWidgetSettings(prev => ({ ...prev, button_text_color: e.target.value }))}
                                className="w-12 h-10 p-1 cursor-pointer flex-shrink-0"
                              />
                              <Input
                                value={widgetSettings.button_text_color}
                                onChange={(e) => setWidgetSettings(prev => ({ ...prev, button_text_color: e.target.value }))}
                                className="flex-1 min-w-0"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Background Colour</Label>
                            <div className="flex gap-2">
                              <Input
                                type="color"
                                value={widgetSettings.background_color}
                                onChange={(e) => setWidgetSettings(prev => ({ ...prev, background_color: e.target.value }))}
                                className="w-12 h-10 p-1 cursor-pointer flex-shrink-0"
                              />
                              <Input
                                value={widgetSettings.background_color}
                                onChange={(e) => setWidgetSettings(prev => ({ ...prev, background_color: e.target.value }))}
                                className="flex-1 min-w-0"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Text Colour</Label>
                            <div className="flex gap-2">
                              <Input
                                type="color"
                                value={widgetSettings.text_color}
                                onChange={(e) => setWidgetSettings(prev => ({ ...prev, text_color: e.target.value }))}
                                className="w-12 h-10 p-1 cursor-pointer flex-shrink-0"
                              />
                              <Input
                                value={widgetSettings.text_color}
                                onChange={(e) => setWidgetSettings(prev => ({ ...prev, text_color: e.target.value }))}
                                className="flex-1 min-w-0"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Accent Colour</Label>
                            <div className="flex gap-2">
                              <Input
                                type="color"
                                value={widgetSettings.accent_color}
                                onChange={(e) => setWidgetSettings(prev => ({ ...prev, accent_color: e.target.value }))}
                                className="w-12 h-10 p-1 cursor-pointer flex-shrink-0"
                              />
                              <Input
                                value={widgetSettings.accent_color}
                                onChange={(e) => setWidgetSettings(prev => ({ ...prev, accent_color: e.target.value }))}
                                className="flex-1 min-w-0"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label>Border Radius</Label>
                            <Select 
                              value={widgetSettings.border_radius} 
                              onValueChange={(v) => setWidgetSettings(prev => ({ ...prev, border_radius: v }))}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="0px">Square</SelectItem>
                                <SelectItem value="4px">Slightly Rounded</SelectItem>
                                <SelectItem value="8px">Rounded</SelectItem>
                                <SelectItem value="12px">More Rounded</SelectItem>
                                <SelectItem value="9999px">Pill</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>

                      {/* Display Options */}
                      <div className="space-y-4">
                        <h4 className="font-medium">Display Options</h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div className="flex items-center justify-between p-3 rounded-lg border">
                            <Label className="text-sm">Show Instructor</Label>
                            <Switch 
                              checked={widgetSettings.show_instructor}
                              onCheckedChange={(checked) => setWidgetSettings(prev => ({ ...prev, show_instructor: checked }))}
                            />
                          </div>
                          <div className="flex items-center justify-between p-3 rounded-lg border">
                            <Label className="text-sm">Show Spots Remaining</Label>
                            <Switch 
                              checked={widgetSettings.show_spots_remaining}
                              onCheckedChange={(checked) => setWidgetSettings(prev => ({ ...prev, show_spots_remaining: checked }))}
                            />
                          </div>
                          <div className="flex items-center justify-between p-3 rounded-lg border">
                            <Label className="text-sm">Show Activity Type</Label>
                            <Switch 
                              checked={widgetSettings.show_activity_type}
                              onCheckedChange={(checked) => setWidgetSettings(prev => ({ ...prev, show_activity_type: checked }))}
                            />
                          </div>
                        </div>
                      </div>

                      {/* Preview */}
                      <div className="space-y-4">
                        <h4 className="font-medium">Button Preview</h4>
                        <div className="p-8 rounded-lg border bg-gray-100 flex justify-center">
                          <button
                            style={{
                              padding: '12px 24px',
                              background: widgetSettings.button_color,
                              color: widgetSettings.button_text_color,
                              border: 'none',
                              borderRadius: widgetSettings.border_radius,
                              cursor: 'pointer',
                              fontFamily: widgetSettings.font_family,
                              fontSize: '16px',
                              fontWeight: 600,
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                            }}
                          >
                            Book a Class
                          </button>
                        </div>
                      </div>

                      <Button onClick={handleSaveWidgetSettings} disabled={widgetLoading} data-testid="save-widget-btn">
                        {widgetLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                        Save Widget Settings
                      </Button>

                      {/* Embed Code */}
                      <div className="space-y-4 pt-4 border-t">
                        <h4 className="font-medium flex items-center gap-2">
                          <Code className="w-5 h-5" />
                          Embed Code
                        </h4>
                        <p className="text-sm text-muted-foreground">
                          Copy and paste this JavaScript snippet into your website's HTML, just before the closing &lt;/body&gt; tag.
                        </p>
                        
                        <div className="relative overflow-hidden">
                          <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto max-h-64 whitespace-pre-wrap break-all">
                            {generateJsSnippet()}
                          </pre>
                          <Button
                            size="sm"
                            variant="secondary"
                            className="absolute top-2 right-2"
                            onClick={() => handleCopyEmbedCode(generateJsSnippet())}
                            data-testid="copy-js-snippet-btn"
                          >
                            <Copy className="w-4 h-4 mr-1" />
                            Copy
                          </Button>
                        </div>

                        {/* Iframe Alternative */}
                        <div className="space-y-2 pt-4">
                          <h5 className="text-sm font-medium">Alternative: iFrame Embed</h5>
                          <p className="text-xs text-muted-foreground">
                            If you prefer to embed the calendar directly into your page:
                          </p>
                          <div className="relative overflow-hidden">
                            <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg text-xs overflow-x-auto whitespace-pre-wrap break-all">
                              {embedCode}
                            </pre>
                            <Button
                              size="sm"
                              variant="secondary"
                              className="absolute top-2 right-2"
                              onClick={() => handleCopyEmbedCode(embedCode)}
                              data-testid="copy-iframe-btn"
                            >
                              <Copy className="w-4 h-4 mr-1" />
                              Copy
                            </Button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            </div>
            )}
          </TabsContent>
        </Tabs>

        {/* Edit Template Dialog */}
        <Dialog open={!!editingChannel} onOpenChange={(open) => !open && setEditingChannel(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {editingChannel && CHANNEL_ICONS[editingChannel.channel] && 
                  React.createElement(CHANNEL_ICONS[editingChannel.channel], { className: "w-5 h-5" })}
                Edit {editingChannel?.channel?.charAt(0).toUpperCase() + editingChannel?.channel?.slice(1)} Template
              </DialogTitle>
              <DialogDescription>
                {EVENT_TYPE_LABELS[editingChannel?.eventType]}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="flex items-center justify-between">
                <Label>Template Active</Label>
                <Switch
                  checked={editForm.is_active}
                  onCheckedChange={(checked) => setEditForm(prev => ({ ...prev, is_active: checked }))}
                />
              </div>

              {editingChannel?.channel === 'email' && (
                <div className="space-y-2">
                  <Label>Email Subject</Label>
                  <Input
                    value={editForm.subject}
                    onChange={(e) => setEditForm(prev => ({ ...prev, subject: e.target.value }))}
                    placeholder="Booking Confirmed - {{ClassTitle}}"
                    data-testid="template-subject-input"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Message Content</Label>
                <Textarea
                  value={editForm.content}
                  onChange={(e) => setEditForm(prev => ({ ...prev, content: e.target.value }))}
                  placeholder="Hi {{ClientFirstName}}, your booking has been confirmed..."
                  rows={8}
                  className="font-mono text-sm"
                  data-testid="template-content-input"
                />
                <p className="text-xs text-muted-foreground">
                  {editingChannel?.channel === 'sms' && 'SMS: Max 160 characters recommended for single message'}
                  {editingChannel?.channel === 'whatsapp' && 'WhatsApp: Must be approved by Meta before sending'}
                </p>
              </div>

              <div className="space-y-2">
                <Label>Insert Variable</Label>
                <div className="flex flex-wrap gap-1">
                  {Object.keys(variables).map(variable => (
                    <Button
                      key={variable}
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => insertVariable(variable)}
                      className="text-xs"
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      {variable}
                    </Button>
                  ))}
                </div>
              </div>
            </div>

            <DialogFooter className="flex gap-2">
              <Button variant="ghost" onClick={handleResetToDefault} disabled={saving}>
                <RotateCcw className="w-4 h-4 mr-2" />
                Reset to Default
              </Button>
              <Button onClick={handleSaveTemplate} disabled={saving}>
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Template
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Test Dialog */}
        <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Send Test Message</DialogTitle>
              <DialogDescription>
                A test message will be sent to your registered email/phone number.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <p className="text-sm text-muted-foreground">
                This will send a {testingTemplate?.channel} with sample data to verify your template works correctly.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTestDialog(false)}>Cancel</Button>
              <Button onClick={handleTestTemplate} disabled={testLoading}>
                {testLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <TestTube2 className="w-4 h-4 mr-2" />}
                Send Test
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* WhatsApp Template Submit Dialog */}
        <Dialog open={whatsappDialogOpen} onOpenChange={setWhatsappDialogOpen}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Submit WhatsApp Template for Approval</DialogTitle>
              <DialogDescription>
                Templates are reviewed by Meta (via Brevo) and typically approved within 1–2 business days.
                Approved templates can be used for booking confirmations, reminders, and waitlist alerts.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="wa-template-name">Template name</Label>
                <Input
                  id="wa-template-name"
                  value={whatsappForm.template_name}
                  onChange={(e) => setWhatsappForm({ ...whatsappForm, template_name: e.target.value.toLowerCase() })}
                  placeholder="booking_confirmation"
                  data-testid="whatsapp-template-name-input"
                />
                <p className="text-xs text-muted-foreground">
                  Lowercase letters, numbers and underscores only. This cannot be changed after submission.
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="wa-template-language">Language</Label>
                <Select
                  value={whatsappForm.language}
                  onValueChange={(v) => setWhatsappForm({ ...whatsappForm, language: v })}
                >
                  <SelectTrigger id="wa-template-language" data-testid="whatsapp-template-language-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="en_GB">English (UK)</SelectItem>
                    <SelectItem value="en_US">English (US)</SelectItem>
                    <SelectItem value="fr">French</SelectItem>
                    <SelectItem value="de">German</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                    <SelectItem value="it">Italian</SelectItem>
                    <SelectItem value="nl">Dutch</SelectItem>
                    <SelectItem value="pt_PT">Portuguese</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="wa-template-content">Template content</Label>
                <Textarea
                  id="wa-template-content"
                  value={whatsappForm.template_content}
                  onChange={(e) => setWhatsappForm({ ...whatsappForm, template_content: e.target.value })}
                  placeholder={"Hi {{1}}, your booking for {{2}} at {{3}} is confirmed."}
                  rows={5}
                  data-testid="whatsapp-template-content-input"
                />
                <p className="text-xs text-muted-foreground">
                  Use <code className="text-xs">{"{{1}}"}</code>, <code className="text-xs">{"{{2}}"}</code> … for variable placeholders. Meta requires transactional (non-promotional) content.
                </p>
              </div>
              <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-900">
                <strong>Before submitting:</strong> You need a Brevo account connected to a verified WhatsApp Business Account (WABA)
                and a verified phone number. Meta will reject templates that are promotional, violate policy, or contain spelling errors.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setWhatsappDialogOpen(false)}>Cancel</Button>
              <Button
                onClick={handleSubmitWhatsappTemplate}
                disabled={whatsappSubmitting}
                data-testid="whatsapp-template-confirm-submit-btn"
              >
                {whatsappSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Submit for approval
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};
